import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiKey,
  FiShield,
  FiLoader,
  FiRefreshCw,
  FiAlertTriangle,
  FiLock,
  FiEye,
  FiEyeOff,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import { createAuthAxios } from "../../../utils/apiUtils";
import { toast } from "react-toastify";
import "./EncryptionSettings.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const EncryptionSettings = () => {
  const [keyStatus, setKeyStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [showRotateForm, setShowRotateForm] = useState(false);

  useEffect(() => {
    fetchKeyStatus();
  }, []);

  const fetchKeyStatus = async () => {
    setIsLoading(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get("/encryption/rotation-status");

      if (response.data.success) {
        setKeyStatus(response.data.data);
      }
    } catch (error) { /* ignored */ } finally {
      setIsLoading(false);
    }
  };

  const generateKeys = async () => {
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setIsGenerating(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post("/encryption/generate-keys", {
        password,
      });

      if (response.data.success) {
        toast.success("Encryption keys generated successfully!");
        setPassword("");
        setShowGenerateForm(false);
        fetchKeyStatus();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Failed to generate encryption keys"
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const rotateKeys = async () => {
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setIsRotating(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post("/encryption/rotate-keys", {
        password,
      });

      if (response.data.success) {
        toast.success("Encryption keys rotated successfully!");
        setPassword("");
        setShowRotateForm(false);
        fetchKeyStatus();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Failed to rotate encryption keys"
      );
    } finally {
      setIsRotating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="stg-content-wrapper"
      >
        <div className="stg-loading" style={{ minHeight: "300px" }}>
          <div className="stg-spinner"></div>
        </div>
      </motion.div>
    );
  }

  const hasKeys = keyStatus && keyStatus.keyGeneratedAt;
  const needsRotation = keyStatus?.needsRotation;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="stg-content-wrapper"
    >
      {/* Section Header */}
      <div className="stg-section-header">
        <h1 className="stg-section-title">
          <span className="stg-section-title-icon stg-green">
            <FiKey />
          </span>
          Encryption Settings
        </h1>
        <p className="stg-section-description">
          Manage your end-to-end encryption keys for secure messaging
        </p>
      </div>

      {/* Key Status Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiShield /> Encryption Status
          </h3>
          <div className="stg-card-actions">
            <button
              className="stg-btn stg-btn-ghost stg-btn-sm"
              onClick={fetchKeyStatus}
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </div>
        <div className="stg-card-body">
          {hasKeys ? (
            <div className="stg-key-status">
              <div className="stg-key-icon">
                <FiCheckCircle />
              </div>
              <div className="stg-key-info">
                <h4 className="stg-key-title">Encryption Keys Active</h4>
                <p className="stg-key-meta">
                  <FiClock size={14} style={{ marginRight: "6px" }} />
                  Generated on {formatDate(keyStatus.keyGeneratedAt)}
                </p>
                {keyStatus.keyRotateAt && (
                  <p className="stg-key-meta" style={{ marginTop: "4px" }}>
                    Next rotation: {formatDate(keyStatus.keyRotateAt)}
                    {keyStatus.daysUntilRotation && (
                      <span
                        style={{
                          marginLeft: "8px",
                          color: "var(--stg-slate-400)",
                        }}
                      >
                        ({keyStatus.daysUntilRotation} days)
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="stg-alert stg-alert-warning">
              <FiAlertTriangle className="stg-alert-icon" />
              <div className="stg-alert-content">
                <p className="stg-alert-title">No Encryption Keys</p>
                <p className="stg-alert-message">
                  You haven't generated encryption keys yet. Generate keys to
                  enable end-to-end encrypted messaging.
                </p>
              </div>
            </div>
          )}

          {needsRotation && (
            <div
              className="stg-alert stg-alert-warning"
              style={{ marginTop: "16px" }}
            >
              <FiRefreshCw className="stg-alert-icon" />
              <div className="stg-alert-content">
                <p className="stg-alert-title">Key Rotation Recommended</p>
                <p className="stg-alert-message">
                  Your encryption keys are due for rotation. Rotate them
                  regularly to maintain security.
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Generate Keys Card (if no keys) */}
      {!hasKeys && (
        <motion.div variants={itemVariants} className="stg-card">
          <div className="stg-card-header">
            <h3 className="stg-card-title">
              <FiKey /> Generate Encryption Keys
            </h3>
          </div>
          <div className="stg-card-body">
            <p style={{ color: "var(--stg-slate-600)", marginBottom: "16px" }}>
              Generating encryption keys will enable end-to-end encrypted
              messaging. You'll need to enter your password to generate the
              keys.
            </p>

            {showGenerateForm ? (
              <div>
                <div className="stg-form-group">
                  <label className="stg-form-label">
                    <FiLock /> Enter your password
                  </label>
                  <div className="stg-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="stg-input"
                      placeholder="Your password"
                      style={{ paddingRight: "48px" }}
                    />
                    <button
                      type="button"
                      className="stg-input-action"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
                <div className="stg-flex stg-gap-md">
                  <button
                    className="stg-btn stg-btn-secondary"
                    onClick={() => {
                      setShowGenerateForm(false);
                      setPassword("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="stg-btn stg-btn-success"
                    onClick={generateKeys}
                    disabled={isGenerating || !password}
                  >
                    {isGenerating ? (
                      <>
                        <FiLoader className="stg-spin" /> Generating...
                      </>
                    ) : (
                      <>
                        <FiKey /> Generate Keys
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="stg-btn stg-btn-success"
                onClick={() => setShowGenerateForm(true)}
              >
                <FiKey /> Generate Encryption Keys
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Rotate Keys Card (if has keys) */}
      {hasKeys && (
        <motion.div variants={itemVariants} className="stg-card">
          <div className="stg-card-header">
            <h3 className="stg-card-title">
              <FiRefreshCw /> Rotate Encryption Keys
            </h3>
          </div>
          <div className="stg-card-body">
            <p style={{ color: "var(--stg-slate-600)", marginBottom: "16px" }}>
              Rotating your encryption keys periodically enhances security.
              After rotation, all your new messages will use the new keys.
            </p>

            {showRotateForm ? (
              <div>
                <div className="stg-form-group">
                  <label className="stg-form-label">
                    <FiLock /> Enter your password to rotate keys
                  </label>
                  <div className="stg-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="stg-input"
                      placeholder="Your password"
                      style={{ paddingRight: "48px" }}
                    />
                    <button
                      type="button"
                      className="stg-input-action"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
                <div className="stg-flex stg-gap-md">
                  <button
                    className="stg-btn stg-btn-secondary"
                    onClick={() => {
                      setShowRotateForm(false);
                      setPassword("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="stg-btn stg-btn-primary"
                    onClick={rotateKeys}
                    disabled={isRotating || !password}
                  >
                    {isRotating ? (
                      <>
                        <FiLoader className="stg-spin" /> Rotating...
                      </>
                    ) : (
                      <>
                        <FiRefreshCw /> Rotate Keys
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="stg-btn stg-btn-secondary"
                onClick={() => setShowRotateForm(true)}
              >
                <FiRefreshCw /> Rotate Encryption Keys
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Info Card */}
      <motion.div variants={itemVariants}>
        <div className="stg-alert stg-alert-info">
          <FiShield className="stg-alert-icon" />
          <div className="stg-alert-content">
            <p className="stg-alert-title">About End-to-End Encryption</p>
            <p className="stg-alert-message">
              End-to-end encryption ensures that only you and the people you're
              communicating with can read your messages. No one else, including
              ResearchPro, can access the content of your encrypted messages.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EncryptionSettings;
